/**
 * Las cinco animaciones de fondo del login en celular. Todas dibujan en un
 * canvas 2D; las piezas que caen o suben se dibujan una sola vez como sprite
 * (con su sombra, que es lo caro) y en cada frame sólo se copian con drawImage.
 */
import { KITCHEN_TICKETS, TICKET_STATE_COLOR, isLate, mmss } from '../../showcase/showcaseData';

export type VariantKey = 'comandas' | 'ingredientes' | 'tickets' | 'platillos' | 'utensilios';

export const VARIANT_KEYS: VariantKey[] = ['comandas', 'ingredientes', 'tickets', 'platillos', 'utensilios'];

export interface Stage {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  dpr: number;
  /** --color-primary del tema, para los trazos de los utensilios. */
  primary: string;
}

export interface Variant {
  /** Reparte las piezas; se llama al montar y cuando cambia el ancho. */
  seed: (stage: Stage) => void;
  frame: (stage: Stage, move: boolean) => void;
}

const TAU = Math.PI * 2;

// Pseudoazar estable por (a, b): el mismo dibujo en cada sprite y en cada frame.
const hash = (a: number, b: number) => {
  const x = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return x - Math.floor(x);
};

const dot = (c: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string) => {
  c.beginPath();
  c.arc(x, y, r, 0, TAU);
  c.fillStyle = fill;
  c.fill();
};

/* ------------------------------------------------------------------ */
/* Sprites                                                             */
/* ------------------------------------------------------------------ */

interface SpriteDef {
  /** Caja del sprite, ya con el margen para la sombra. Se dibuja centrado en 0,0. */
  size: number;
  draw: (c: CanvasRenderingContext2D) => void;
}

interface Sprite {
  img: HTMLCanvasElement;
  size: number;
}

const makeSprite = (def: SpriteDef, res: number): Sprite => {
  const img = document.createElement('canvas');
  img.width = Math.ceil(def.size * res);
  img.height = Math.ceil(def.size * res);
  const c = img.getContext('2d');
  if (c) {
    c.scale(res, res);
    c.translate(def.size / 2, def.size / 2);
    def.draw(c);
  }
  return { img, size: def.size };
};

/* ---------- comandas ---------- */

const comandaSprite = (k: number): SpriteDef => ({
  size: 124,
  draw: (c) => {
    const W = 60;
    const H = 78;
    const ticket = KITCHEN_TICKETS[k];
    const color = TICKET_STATE_COLOR[ticket.state];
    c.shadowColor = 'rgba(0, 0, 0, 0.5)';
    c.shadowBlur = 16;
    c.shadowOffsetY = 6;
    c.fillStyle = '#fffdf6';
    c.fillRect(-W / 2, -H / 2, W, H);
    c.shadowColor = 'transparent';
    c.fillStyle = color;
    c.fillRect(-W / 2, -H / 2, W, 5);
    c.font = '600 7.5px ui-monospace, Menlo, Consolas, monospace';
    c.fillStyle = '#1d2523';
    c.fillText(ticket.title, -W / 2 + 5, -H / 2 + 16, W - 10);
    c.fillStyle = '#d3d8d6';
    c.fillRect(-W / 2 + 5, -H / 2 + 21, W - 10, 1);
    for (let i = 0; i < 4; i++) {
      c.fillStyle = '#1d2523';
      c.fillRect(-W / 2 + 5, -H / 2 + 27 + i * 9, 3, 3);
      c.fillStyle = '#9aa5a2';
      c.fillRect(-W / 2 + 11, -H / 2 + 27 + i * 9, 16 + hash(k, i) * 26, 3);
    }
    c.fillStyle = color;
    c.fillRect(-W / 2 + 5, H / 2 - 9, 20, 3);
    c.fillStyle = isLate(ticket, ticket.seconds) ? '#c0392b' : '#5b6b68';
    c.fillText(mmss(ticket.seconds), W / 2 - 25, H / 2 - 5);
  },
});

/* ---------- ingredientes ---------- */

const INGREDIENTS: Array<(c: CanvasRenderingContext2D) => void> = [
  // limón
  (c) => {
    dot(c, 0, 0, 16, '#6fa632');
    dot(c, 0, 0, 13.5, '#d6ec98');
    c.strokeStyle = '#f5fbe2';
    c.lineWidth = 1.4;
    for (let a = 0; a < 8; a++) {
      const an = (a * TAU) / 8;
      c.beginPath();
      c.moveTo(0, 0);
      c.lineTo(Math.cos(an) * 13, Math.sin(an) * 13);
      c.stroke();
    }
    dot(c, 0, 0, 2.2, '#f5fbe2');
  },
  // jitomate
  (c) => {
    dot(c, 0, 0, 15, '#de4434');
    c.beginPath();
    c.ellipse(-5, -5, 4.5, 3, -0.6, 0, TAU);
    c.fillStyle = 'rgba(255, 255, 255, 0.35)';
    c.fill();
    c.fillStyle = '#3f8a2e';
    for (let a = 0; a < 5; a++) {
      c.save();
      c.translate(0, -13);
      c.rotate((a * TAU) / 5);
      c.beginPath();
      c.ellipse(0, -4, 2, 5, 0, 0, TAU);
      c.fill();
      c.restore();
    }
  },
  // chile
  (c) => {
    c.beginPath();
    c.moveTo(-18, -5);
    c.quadraticCurveTo(2, -15, 22, 8);
    c.quadraticCurveTo(4, 2, -18, 6);
    c.closePath();
    c.fillStyle = '#c42f26';
    c.fill();
    c.lineCap = 'round';
    c.beginPath();
    c.moveTo(-14, -6);
    c.quadraticCurveTo(0, -11, 10, -3);
    c.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    c.lineWidth = 2;
    c.stroke();
    c.beginPath();
    c.moveTo(-18, 0);
    c.quadraticCurveTo(-24, -2, -26, -9);
    c.strokeStyle = '#3f8a2e';
    c.lineWidth = 3.2;
    c.stroke();
  },
  // aguacate
  (c) => {
    c.beginPath();
    c.ellipse(0, 0, 13, 17, 0, 0, TAU);
    c.fillStyle = '#35602a';
    c.fill();
    c.beginPath();
    c.ellipse(0, 0.5, 10.5, 14.5, 0, 0, TAU);
    c.fillStyle = '#d3e38c';
    c.fill();
    dot(c, 0, 4, 6.2, '#8b5a2b');
    dot(c, -1.8, 2.4, 1.8, 'rgba(255, 255, 255, 0.3)');
  },
  // cilantro
  (c) => {
    c.strokeStyle = '#3f7f2a';
    c.lineWidth = 1.6;
    c.beginPath();
    c.moveTo(0, 4);
    c.lineTo(0, 16);
    c.stroke();
    [[-90, 6], [-20, 6], [200, 6], [-140, 9], [-40, 9]].forEach(([deg, r]) => {
      const a = (deg * Math.PI) / 180;
      dot(c, Math.cos(a) * r, Math.sin(a) * r, 5.2, '#4f9a35');
    });
  },
  // cebolla morada
  (c) => {
    c.lineWidth = 3.2;
    c.strokeStyle = '#c99fd6';
    c.beginPath();
    c.arc(0, 0, 12, 0, TAU);
    c.stroke();
    c.lineWidth = 2;
    c.strokeStyle = '#e5cfee';
    c.beginPath();
    c.arc(0, 0, 7.5, 0, TAU);
    c.stroke();
  },
];

/* ---------- platillos vistos desde arriba ---------- */

const DISHES: Array<(c: CanvasRenderingContext2D) => void> = [
  // tacos
  (c) => {
    [[-14, 8, -0.5], [0, -10, 0], [14, 8, 0.5]].forEach(([x, y, r]) => {
      c.save();
      c.translate(x, y);
      c.rotate(r);
      c.beginPath();
      c.ellipse(0, 0, 14, 9, 0, 0, TAU);
      c.fillStyle = '#e8c46a';
      c.fill();
      c.beginPath();
      c.ellipse(0, 0, 10, 5, 0, 0, TAU);
      c.fillStyle = '#b5451f';
      c.fill();
      dot(c, -4, -1, 1.8, '#5f9e3a');
      dot(c, 3, 1, 1.8, '#f6efdf');
      dot(c, 5, -2, 1.5, '#5f9e3a');
      c.restore();
    });
    c.beginPath();
    c.moveTo(24, -20);
    c.arc(24, -20, 9, 2.0, 4.6);
    c.closePath();
    c.fillStyle = '#8dbb3e';
    c.fill();
  },
  // pozole
  (c) => {
    dot(c, 0, 0, 31, '#efe6d4');
    dot(c, 0, 0, 27, '#b5351f');
    [[-9, -6], [6, -11], [11, 5], [-4, 9], [-15, 4], [2, 1], [14, -12]].forEach(([x, y]) => dot(c, x, y, 2.7, '#f6efdf'));
    dot(c, -6, -15, 4.2, '#f8dde0');
    dot(c, 13, -3, 3.6, '#f8dde0');
    [[4, 13], [-13, -3], [8, -6]].forEach(([x, y]) => {
      c.beginPath();
      c.ellipse(x, y, 4.2, 1.8, 0.6, 0, TAU);
      c.fillStyle = '#4f8f2f';
      c.fill();
    });
  },
  // enchiladas
  (c) => {
    c.save();
    c.rotate(-0.35);
    for (let i = -1; i <= 1; i++) {
      c.fillStyle = '#4f8f3a';
      c.fillRect(-25, i * 12 - 5, 50, 10);
    }
    c.strokeStyle = '#fbf6ec';
    c.lineWidth = 2.2;
    c.lineJoin = 'round';
    c.beginPath();
    for (let k = 0, x = -22; x <= 22; x += 5.5, k++) {
      if (k) c.lineTo(x, k % 2 ? 13 : -13);
      else c.moveTo(x, -13);
    }
    c.stroke();
    [[-12, -6], [4, 8], [14, -4], [-4, 2]].forEach(([x, y]) => dot(c, x, y, 2.2, '#c99fd6'));
    c.restore();
  },
  // guacamole en molcajete con totopos
  (c) => {
    for (let a = 0; a < 6; a++) {
      c.save();
      c.rotate((a * TAU) / 6 + 0.3);
      c.beginPath();
      c.moveTo(-7, -27);
      c.lineTo(7, -27);
      c.lineTo(0, -42);
      c.closePath();
      c.fillStyle = '#e8b84a';
      c.fill();
      c.restore();
    }
    dot(c, 0, 0, 29, '#4a4a46');
    dot(c, 0, 0, 22, '#a3c24f');
    [[-8, -5], [6, -9], [9, 6], [-5, 10], [0, 0]].forEach(([x, y]) => dot(c, x, y, 2.4, '#d24a3a'));
    [[-12, 4], [12, -2], [2, -14]].forEach(([x, y]) => dot(c, x, y, 1.8, '#f3eedf'));
  },
  // flan
  (c) => {
    dot(c, 0, 0, 20, '#e9b35a');
    dot(c, 0, 0, 14, '#b9652a');
    dot(c, -4, -4, 4, 'rgba(255, 255, 255, 0.3)');
    c.beginPath();
    c.ellipse(7, -12, 5, 2.4, -0.6, 0, TAU);
    c.fillStyle = '#3f8a2e';
    c.fill();
  },
];

const plateSprite = (dish: (c: CanvasRenderingContext2D) => void): SpriteDef => ({
  size: 150,
  draw: (c) => {
    c.shadowColor = 'rgba(0, 0, 0, 0.22)';
    c.shadowBlur = 18;
    c.shadowOffsetY = 6;
    dot(c, 0, 0, 46, '#fbfaf6');
    c.shadowColor = 'transparent';
    c.strokeStyle = '#e6e0d2';
    c.lineWidth = 1.5;
    c.beginPath();
    c.arc(0, 0, 37, 0, TAU);
    c.stroke();
    dish(c);
  },
});

/* ---------- utensilios en línea ---------- */

const UTENSILS: Array<(c: CanvasRenderingContext2D) => void> = [
  // tenedor
  (c) => {
    c.moveTo(0, 24);
    c.lineTo(0, -2);
    c.moveTo(-7, -22);
    c.lineTo(-7, -9);
    c.quadraticCurveTo(-7, -2, 0, -2);
    c.quadraticCurveTo(7, -2, 7, -9);
    c.lineTo(7, -22);
    c.moveTo(-2.3, -22);
    c.lineTo(-2.3, -9);
    c.moveTo(2.3, -22);
    c.lineTo(2.3, -9);
  },
  // cuchillo
  (c) => {
    c.moveTo(-3, 4);
    c.lineTo(-3, -24);
    c.quadraticCurveTo(6, -18, 4, 4);
    c.closePath();
    c.rect(-3.5, 4, 7, 20);
  },
  // cuchara
  (c) => {
    c.ellipse(0, -14, 7, 10, 0, 0, TAU);
    c.moveTo(0, -4);
    c.lineTo(0, 24);
  },
  // gorro de chef
  (c) => {
    c.moveTo(-11, 6);
    c.lineTo(-11, -5);
    c.bezierCurveTo(-21, -7, -16, -21, -6, -15);
    c.bezierCurveTo(-4, -25, 6, -25, 7, -15);
    c.bezierCurveTo(16, -21, 21, -7, 11, -5);
    c.lineTo(11, 6);
    c.closePath();
    c.moveTo(-11, 0);
    c.lineTo(11, 0);
  },
  // taza de café
  (c) => {
    c.moveTo(-10, -6);
    c.lineTo(-8, 14);
    c.lineTo(8, 14);
    c.lineTo(10, -6);
    c.closePath();
    c.moveTo(10, -1);
    c.arc(11, 4, 5, -Math.PI / 2, Math.PI / 2);
    c.moveTo(-3, -11);
    c.bezierCurveTo(-6, -15, 0, -17, -3, -22);
    c.moveTo(4, -11);
    c.bezierCurveTo(1, -15, 7, -17, 4, -22);
  },
  // campana para servir
  (c) => {
    c.arc(0, 8, 17, Math.PI, 0);
    c.moveTo(-22, 8);
    c.lineTo(22, 8);
    c.moveTo(2.5, -11);
    c.arc(0, -11, 2.5, 0, TAU);
  },
];

const utensilSprite = (path: (c: CanvasRenderingContext2D) => void, color: string): SpriteDef => ({
  size: 64,
  draw: (c) => {
    c.strokeStyle = color;
    c.lineWidth = 1.8;
    c.lineCap = 'round';
    c.lineJoin = 'round';
    c.beginPath();
    path(c);
    c.stroke();
  },
});

/* ------------------------------------------------------------------ */
/* Piezas que caen (o suben)                                           */
/* ------------------------------------------------------------------ */

interface Piece {
  sprite: number;
  x: number;
  y: number;
  scale: number;
  rot: number;
  spin: number;
  speed: number;
  drift: number;
  depth: number;
  phase: number;
}

interface FallerOptions {
  sprites: (stage: Stage) => SpriteDef[];
  /** Una pieza por cada tantos px² de pantalla, entre min y max. */
  density: number;
  min: number;
  max: number;
  scale: [number, number];
  rot: number;
  spin: number;
  speed: [number, number];
  /** 1 cae, -1 sube. */
  dir: 1 | -1;
  sway?: number;
  alpha: (depth: number) => number;
}

const between = ([a, b]: [number, number]) => a + Math.random() * (b - a);

const fallers = (o: FallerOptions): Variant => {
  let pieces: Piece[] = [];
  let sprites: Sprite[] = [];
  let spritesFor = '';

  return {
    seed: (st) => {
      const key = `${st.dpr}|${st.primary}`;
      if (key !== spritesFor) {
        sprites = o.sprites(st).map((def) => makeSprite(def, st.dpr * o.scale[1]));
        spritesFor = key;
      }
      const n = Math.max(o.min, Math.min(o.max, Math.round((st.width * st.height) / o.density)));
      pieces = Array.from({ length: n }, (_, i) => ({
        sprite: i % sprites.length,
        x: Math.random() * st.width,
        y: Math.random() * st.height,
        scale: between(o.scale),
        rot: (Math.random() - 0.5) * o.rot,
        spin: (Math.random() - 0.5) * o.spin,
        speed: between(o.speed),
        drift: (Math.random() - 0.5) * 0.12,
        depth: 0.4 + Math.random() * 0.6,
        phase: Math.random() * TAU,
      })).sort((a, b) => a.depth - b.depth);
    },
    frame: (st, move) => {
      const c = st.ctx;
      c.clearRect(0, 0, st.width, st.height);
      for (const p of pieces) {
        const sprite = sprites[p.sprite];
        const half = (sprite.size / 2) * p.scale;
        if (move) {
          p.y += o.dir * p.speed * (0.5 + p.depth);
          p.x += p.drift;
          p.rot += p.spin;
          p.phase += 0.02;
          if (o.dir > 0 && p.y > st.height + half) {
            p.y = -half;
            p.x = Math.random() * st.width;
          } else if (o.dir < 0 && p.y < -half) {
            p.y = st.height + half;
            p.x = Math.random() * st.width;
          }
          if (p.x < -half) p.x = st.width + half;
          else if (p.x > st.width + half) p.x = -half;
        }
        c.save();
        c.globalAlpha = o.alpha(p.depth);
        c.translate(p.x, p.y);
        c.rotate(p.rot + (o.sway ? Math.sin(p.phase) * o.sway : 0));
        c.scale(p.scale, p.scale);
        c.drawImage(sprite.img, -sprite.size / 2, -sprite.size / 2, sprite.size, sprite.size);
        c.restore();
      }
    },
  };
};

/* ------------------------------------------------------------------ */
/* Papel de impresora de caja (columnas que suben en diagonal)         */
/* ------------------------------------------------------------------ */

const receiptTape = (): Variant => {
  let cols: Array<{ x: number; off: number; speed: number; seed: number }> = [];
  const ROW = 12;
  const LEN = 24; // renglones por ticket, incluido el hueco entre uno y otro

  return {
    seed: (st) => {
      cols = [];
      for (let i = -2; i < Math.ceil(st.width / 76) + 2; i++) {
        cols.push({ x: i * 76 + hash(i, 3) * 10, off: hash(i, 7) * 800, speed: 0.3 + hash(i, 9) * 0.45, seed: i + 10 });
      }
    },
    frame: (st, move) => {
      const c = st.ctx;
      c.clearRect(0, 0, st.width, st.height);
      c.save();
      c.translate(st.width / 2, st.height / 2);
      c.rotate(-0.14);
      c.translate(-st.width / 2, -st.height / 2);
      for (const col of cols) {
        if (move) col.off += col.speed;
        const first = Math.floor((col.off - 140) / ROW);
        const last = Math.ceil((col.off + st.height + 140) / ROW);
        for (let i = first; i <= last; i++) {
          const y = i * ROW - col.off;
          const r = ((i % LEN) + LEN) % LEN;
          if (r < 2) continue;
          const ticket = Math.floor(i / LEN);
          const items = 3 + Math.floor(hash(ticket, col.seed) * 5);
          c.fillStyle = 'rgba(253, 252, 247, 0.9)';
          c.fillRect(col.x, y, 62, ROW + 0.5);
          c.fillStyle = '#b3bdba';
          if (r === 3) {
            c.fillStyle = '#4c5a57';
            c.fillRect(col.x + 14, y + 4, 34, 4);
          } else if (r === 4) {
            c.fillRect(col.x + 20, y + 4, 22, 3);
          } else if (r === 6 || r === 8 + items) {
            c.fillRect(col.x + 6, y + 6, 50, 1);
          } else if (r >= 7 && r < 7 + items) {
            c.fillRect(col.x + 6, y + 4, 12 + hash(i, col.seed) * 20, 3);
            c.fillRect(col.x + 44, y + 4, 12, 3);
          } else if (r === 9 + items) {
            c.fillStyle = '#4c5a57';
            c.fillRect(col.x + 6, y + 3, 18, 5);
            c.fillRect(col.x + 36, y + 3, 20, 5);
          } else if (r === LEN - 3) {
            c.fillStyle = '#4c5a57';
            for (let b = 0; b < 12; b++) {
              if (hash(b, ticket + col.seed) > 0.4) c.fillRect(col.x + 8 + b * 4, y + 1, 2, ROW - 1);
            }
          }
        }
      }
      c.restore();
    },
  };
};

/* ------------------------------------------------------------------ */

export const createVariant = (key: VariantKey): Variant => {
  switch (key) {
    case 'comandas':
      return fallers({
        sprites: () => KITCHEN_TICKETS.map((_, k) => comandaSprite(k)),
        density: 16000, min: 9, max: 18,
        scale: [0.75, 1.25], rot: 0.7, spin: 0, speed: [0.3, 0.6], dir: 1, sway: 0.12,
        alpha: (d) => 0.5 + d * 0.5,
      });
    case 'ingredientes':
      return fallers({
        sprites: () => INGREDIENTS.map((draw) => ({ size: 64, draw })),
        density: 9000, min: 14, max: 30,
        scale: [0.8, 1.5], rot: TAU, spin: 0.02, speed: [0.25, 0.55], dir: 1,
        alpha: (d) => 0.55 + d * 0.4,
      });
    case 'tickets':
      return receiptTape();
    case 'platillos':
      return fallers({
        sprites: () => DISHES.map(plateSprite),
        density: 30000, min: 5, max: 9,
        scale: [0.75, 1.15], rot: TAU, spin: 0.006, speed: [0.2, 0.35], dir: -1,
        alpha: () => 1,
      });
    default:
      return fallers({
        sprites: (st) => UTENSILS.map((path) => utensilSprite(path, st.primary)),
        density: 8000, min: 16, max: 34,
        scale: [0.8, 1.5], rot: 1.2, spin: 0.008, speed: [0.15, 0.3], dir: -1,
        alpha: (d) => 0.25 + d * 0.45,
      });
  }
};
